// with-settings-file-lock.ts
//
// #164 — loại trừ lẫn nhau cho MỘT chu kỳ read-modify-write trên
// `<ws>/.claude/settings.json`. `writeJsonAtomic` (tmp+rename) chống FILE RÁCH,
// nhưng KHÔNG chống lost update: 2 tiến trình đọc cùng một snapshot rồi lần lượt
// ghi ⇒ đóng góp của kẻ ghi trước biến mất im lặng.
//
// PHÁT BIỂU ĐÚNG PHẠM VI (chống ảo giác an toàn — đọc trước khi tin vào helper này):
//   ✅ đóng lost update giữa các writer ĐI QUA helper này, kể cả cross-command
//      (`sync` × `tools enable` × `ai setup` × `init`).
//   ❌ KHÔNG đóng run-level interleaving: 2 `avatar sync` song song vẫn đan xen
//      từng bracket; trạng thái cuối là *merge hợp lệ của cả hai*, không phải
//      snapshot của một run.
//   ❌ KHÔNG chặn sửa tay (`vim .claude/settings.json`), KHÔNG chặn CLI bản CŨ
//      (version chưa có lock) — hai thứ đó không tham gia protocol.
//
// BA LỚP, THỨ TỰ KHÔNG ĐƯỢC ĐẢO: kiểm-lồng (throw ngay) → xếp-hàng in-process
// (chờ) → lock file liên-tiến-trình (chờ/thu hồi). Đảo lớp 1 xuống sau lớp 2 thì
// lời gọi lồng sẽ chờ CHÍNH MÌNH trong hàng đợi = self-deadlock.
// Lớp 3 nằm ở acquire-settings-file-lock-raw.ts.

import { AsyncLocalStorage } from "node:async_hooks";
import { join } from "node:path";
import {
  acquireSettingsFileLockRaw,
  DEFAULT_LOCK_TIMEOUT_MS,
  type SettingsFileLockOptions,
  SettingsLockReentrancyError,
  SettingsLockTimeoutError,
} from "./acquire-settings-file-lock-raw.js";
import { AsyncMutexByKey } from "./async-mutex-by-key.js";

// Call site chỉ cần nhớ MỘT module: re-export trọn bộ mặt tiền của lock.
export {
  acquireSettingsFileLockRaw,
  type SettingsFileLockOptions,
  SettingsLockReentrancyError,
  SettingsLockTimeoutError,
};

// MỘT literal "settings.json.lock" DUY NHẤT trong toàn repo.
// ⛔ CẤM ghép `` `${join(ws, ".claude", "settings.json")}.lock` ``: chuỗi
// "settings.json" trong dấu nháy sẽ khớp regex quét của audit gate (phase-03) ⇒
// chính helper lọt vào tập writer ⇒ gate ĐỎ vĩnh viễn, không sửa được mà không
// đụng chính gate.
export function settingsLockPath(workspacePath: string): string {
  return join(workspacePath, ".claude", "settings.json.lock");
}

// Lớp 2: xếp hàng in-process theo lockPath (2 lời gọi ĐỘC LẬP cùng tiến trình).
const queue = new AsyncMutexByKey();
// Lớp 1: tập lock đang giữ TRONG ĐÚNG async context này. Nhánh Promise.all anh-em
// KHÔNG nằm trong `run()` của nhau ⇒ không báo lồng oan; callback lồng thì nằm
// trong ⇒ bắt đúng.
const heldHere = new AsyncLocalStorage<Set<string>>();

export async function withSettingsFileLock<T>(
  workspacePath: string,
  mutator: () => Promise<T>,
  opts: SettingsFileLockOptions = {},
): Promise<T> {
  const lockPath = settingsLockPath(workspacePath);
  const held = heldHere.getStore();
  if (held?.has(lockPath)) {
    throw new SettingsLockReentrancyError(
      `withSettingsFileLock lồng nhau trên ${lockPath} — sẽ tự deadlock. ` +
        "Gom read+write vào MỘT bracket thay vì bọc hai tầng.",
    );
  }
  const timeoutMs = opts.timeoutMs ?? DEFAULT_LOCK_TIMEOUT_MS;
  // Deadline chốt TẠI ĐÂY: ngân sách gồm CẢ thời gian chờ hàng đợi. Bấm giờ sau khi
  // ra khỏi hàng đợi thì một mutator kẹt sẽ treo người xếp sau VÔ HẠN — đúng thứ
  // fail-closed sinh ra để tránh.
  const deadlineAt = Date.now() + timeoutMs;
  const releaseSlot = await queue.acquire(
    lockPath,
    deadlineAt,
    () =>
      new SettingsLockTimeoutError(
        `Chờ quá ${timeoutMs}ms tới lượt ghi settings.json trong tiến trình này (${lockPath}).`,
      ),
  );
  try {
    const lock = await acquireSettingsFileLockRaw(lockPath, { ...opts, deadlineAt });
    try {
      const nextHeld = new Set(held ?? []);
      nextHeld.add(lockPath);
      // Helper KHÔNG nuốt lỗi của mutator (che lỗi ghi settings = che lỗi bảo mật).
      return await heldHere.run(nextHeld, mutator);
    } finally {
      await lock.release();
    }
  } finally {
    releaseSlot();
  }
}
